// ScanCard.tsx — one scan in the Library grid: thumbnail, name, status badge,
// dimensions, and the per-scan actions (open, pin, rename, delete, PLY/OSPL
// download). Actionable, so it carries all three slots with the controls in
// the footer (DESIGN.md C-CARD-1). Rename and delete are delegated to the
// page, which owns the Modal and the confirm dialog. Actions follow the
// caller's access: pin and delete are the owner's, rename needs an editor; a
// shared scan names its owner, and an owner's shared scan is marked Shared.
import { useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import { BsDownload, BsPencil, BsPinAngle, BsPinAngleFill, BsTrash } from 'react-icons/bs';
import { Link } from 'react-router-dom';
import { Badge } from 'common';
import { atLeast, urls } from '../api';
import type { Scan } from '../api';
import { fmtCount, fmtDate, fmtDims } from '../format';
import { ScanStatusBadge } from './ScanStatusBadge';

export interface ScanCardProps {
  scan: Scan;
  onPin: (scan: Scan) => void;
  onRename: (scan: Scan) => void;
  onDelete: (scan: Scan) => void;
}

export function ScanCard({ scan, onPin, onRename, onDelete }: ScanCardProps) {
  const [thumbOk, setThumbOk] = useState(true);
  const href = `/scans/${scan.id}`;
  const owner = atLeast(scan, 'owner');
  const editor = atLeast(scan, 'editor');
  return (
    <Card className="scan-card">
      <Card.Header className="d-flex align-items-center gap-2">
        <Link to={href} className="text-reset text-decoration-none text-truncate flex-grow-1">
          {scan.name}
        </Link>
        {owner && scan.shared && <Badge tone="info">Shared</Badge>}
        <ScanStatusBadge status={scan.status} />
      </Card.Header>
      <Link to={href} aria-label={`Open ${scan.name}`}>
        {thumbOk ? (
          <img
            className="scan-thumb d-block"
            src={urls.thumbnail(scan.id, scan.updated_at)}
            alt=""
            loading="lazy"
            onError={() => setThumbOk(false)}
          />
        ) : (
          <div className="scan-thumb d-flex align-items-center justify-content-center text-body-secondary small">
            {scan.has_seed || scan.has_splats ? 'Preview pending' : 'No preview yet'}
          </div>
        )}
      </Link>
      <Card.Body>
        <div className="measure-value">{fmtDims(scan.dimensions)}</div>
        <div className="small text-body-secondary">
          {fmtDate(scan.created_at)} · {scan.keyframe_count} keyframes · {fmtCount(scan.gaussian_count)} splats
        </div>
        {!owner && (
          <div className="small text-body-secondary text-truncate">
            {scan.owner} · {scan.access === 'editor' ? 'can edit' : 'can view'}
          </div>
        )}
        {scan.tags.length > 0 && <div className="small mt-1">{scan.tags.map((t) => `#${t}`).join(' ')}</div>}
        {scan.status === 'failed' && scan.error && <div className="small text-danger mt-1">{scan.error}</div>}
      </Card.Body>
      <Card.Footer className="d-flex flex-wrap gap-1">
        {owner && (
          <Button size="sm" variant="link" onClick={() => onPin(scan)} aria-label={scan.pinned ? 'Unpin' : 'Pin'}>
            {scan.pinned ? <BsPinAngleFill /> : <BsPinAngle />}
          </Button>
        )}
        {editor && (
          <Button size="sm" variant="outline-secondary" onClick={() => onRename(scan)}>
            <BsPencil className="me-1" />
            Rename
          </Button>
        )}
        <Button size="sm" variant="outline-primary" as="a" href={urls.exportPly(scan.id)} download disabled={!scan.has_splats && !scan.has_seed}>
          <BsDownload className="me-1" />
          PLY
        </Button>
        <Button size="sm" variant="outline-primary" as="a" href={urls.splats(scan.id)} download={`${scan.name}.ospl`} disabled={!scan.has_splats && !scan.has_seed}>
          <BsDownload className="me-1" />
          OSPL
        </Button>
        {owner && (
          <Button size="sm" variant="outline-danger" className="ms-auto" onClick={() => onDelete(scan)}>
            <BsTrash className="me-1" />
            Delete
          </Button>
        )}
      </Card.Footer>
    </Card>
  );
}
